import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { apiFetch } from '../lib/api'

interface AppConfig {
  id?: number
  config_key: string
  config_value: string
  description?: string
  created_at?: string
  updated_at?: string
}

interface ConfigState {
  configs: Record<string, AppConfig>
  isLoading: boolean
  error: string | null

  // Actions
  fetchConfigs: () => Promise<void>
  fetchConfig: (key: string) => Promise<void>
  updateConfig: (key: string, value: string, description?: string) => Promise<void>
  getConfigValue: (key: string, defaultValue?: string) => string
}

export const useConfigStore = create<ConfigState>()(
  devtools(
    persist(
      (set, get) => ({
        configs: {},
        isLoading: false,
        error: null,

        fetchConfigs: async () => {
          set({ isLoading: true, error: null })
          try {
            const response = await apiFetch('/api/config')
            if (!response.ok) throw new Error('Failed to fetch configurations')
            const data = await response.json()
            const configsArray = data.data || []

            const configsMap = configsArray.reduce((acc: Record<string, AppConfig>, config: AppConfig) => {
              acc[config.config_key] = config
              return acc
            }, {})

            set({ configs: configsMap, isLoading: false })
          } catch (error) {
            console.error('Error fetching configs:', error)
            set({
              error: error instanceof Error ? error.message : 'Failed to fetch configurations',
              isLoading: false
            })
          }
        },

        fetchConfig: async (key: string) => {
          set({ isLoading: true, error: null })
          try {
            const response = await apiFetch(`/api/config/${key}`)
            if (response.status === 404) { set({ isLoading: false }); return }
            if (!response.ok) throw new Error(`Failed to fetch configuration '${key}'`)
            const data = await response.json()
            const config = data.data

            set((state) => ({
              configs: {
                ...state.configs,
                [config.config_key]: config
              },
              isLoading: false
            }))
          } catch (error) {
            console.error(`Error fetching config '${key}':`, error)
            set({
              error: error instanceof Error ? error.message : `Failed to fetch configuration '${key}'`,
              isLoading: false
            })
          }
        },

        updateConfig: async (key: string, value: string, description?: string) => {
          set({ isLoading: true, error: null })
          try {
            const response = await apiFetch(`/api/config/${key}`, {
              method: 'PUT',
              body: JSON.stringify({ value, description })
            })
            if (!response.ok) throw new Error(`Failed to update configuration '${key}'`)
            const data = await response.json()
            const config = data.data

            set((state) => ({
              configs: {
                ...state.configs,
                [config.config_key]: config
              },
              isLoading: false
            }))
          } catch (error) {
            console.error(`Error updating config '${key}':`, error)
            set({
              error: error instanceof Error ? error.message : `Failed to update configuration '${key}'`,
              isLoading: false
            })
            throw error
          }
        },

        getConfigValue: (key: string, defaultValue: string = '') => {
          const config = get().configs[key]
          return config?.config_value || defaultValue
        }
      }),
      {
        name: 'config-store'
      }
    ),
    {
      name: 'config-store'
    }
  )
)
